import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useToast } from "@/components/ui/use-toast";
import HeroSection from "@/components/home/HeroSection";
import HowItWorksSection from "@/components/home/HowItWorksSection";
import SearchSection from "@/components/home/SearchSection";
import CategoriesShowcase from "@/components/home/CategoriesShowcase";
import CreateReport from "@/components/CreateReport";
import ReportList from "@/components/ReportList";
import StatsSection from "@/components/home/StatsSection";
import TestimonialsSection from "@/components/home/TestimonialsSection";
import FaqSection from "@/components/home/FaqSection";
import EventsSection from "@/components/home/EventsSection";
import CommunitySection from "@/components/home/CommunitySection";
import FinalCtaSection from "@/components/home/FinalCtaSection";
import Footer from "@/components/landing/Footer";
import MobileBottomNav from "@/components/navigation/MobileBottomNav";
import { scrollIntoViewRespectingMotion, scrollToRespectingMotion } from "@/lib/scroll";

const HOME_FAQS = [
  { q: "Comment signaler un problème dans ma ville ?", a: "Cliquez sur 'Nouveau signalement', remplissez le formulaire avec les détails, ajoutez une photo si possible, et soumettez. Vous recevrez des notifications sur l'avancement." },
  { q: "Comment suivre l'état de mon signalement ?", a: "Tous vos signalements sont visibles dans votre tableau de bord personnel. Vous recevez également des notifications automatiques à chaque changement de statut." },
  { q: "Les mairies peuvent-elles utiliser cette plateforme ?", a: "Oui. KLIKAN propose un accès dédié pour les communes avec des tableaux de bord de gestion spécifiques." },
  { q: "Comment soutenir cette application ?", a: "L'accès est gratuit pour les citoyens. Vous pouvez soutenir KLIKAN en parrainant de nouveaux utilisateurs, en participant à la communauté (ateliers, concours), en utilisant nos liens d'achats partenaires, ou en recommandant l'application à votre municipalité." },
];

const SITE_URL = "https://klikan-fr.lovable.app";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "FAQPage",
      "@id": `${SITE_URL}/#faq`,
      mainEntity: HOME_FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@type": "WebApplication",
      "@id": `${SITE_URL}/#webapp`,
      name: "KLIKAN",
      url: SITE_URL,
      applicationCategory: "GovernmentApplication",
      operatingSystem: "Web, iOS, Android",
      inLanguage: "fr-FR",
      description:
        "Application citoyenne française pour signaler à votre mairie : nids-de-poule, éclairage, voirie, déchets. Signalement en 30 secondes, suivi de résolution en temps réel.",
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
      publisher: { "@id": `${SITE_URL}/#organization` },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.7",
        ratingCount: "128",
        bestRating: "5",
        worstRating: "1",
      },
      featureList: [
        "Signalement en 30 secondes",
        "Suivi temps réel",
        "Notifications de résolution",
        "Carte interactive des signalements",
        "Tableau de bord pour collectivités",
      ],
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${SITE_URL}/#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
      ],
    },
  ],
};

const Index = () => {
  const [showCreate, setShowCreate] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const location = useLocation();
  const [animatedStats, setAnimatedStats] = useState({
    signalements: 0,
    resolutions: 0,
    utilisateurs: 0,
    communes: 0
  });
  const { toast } = useToast();

  const finalStats = {
    signalements: 1234,
    resolutions: 892,
    utilisateurs: 3721,
    communes: 42
  };

  useEffect(() => {
    animateStats();
  }, []);

  // Met le focus sur un élément de manière programmatique sans casser l'ordre Tab.
  // Pose tabIndex={-1} temporairement, puis le nettoie au blur.
  const focusProgrammatically = (el: HTMLElement) => {
    const prevTabIndex = el.getAttribute("tabindex");
    if (prevTabIndex === null) el.setAttribute("tabindex", "-1");
    el.focus({ preventScroll: true });
    const cleanup = () => {
      if (prevTabIndex === null) el.removeAttribute("tabindex");
      el.removeEventListener("blur", cleanup);
    };
    el.addEventListener("blur", cleanup);
  };

  // Scroll + focus vers la section ciblée par le hash (#comment-ca-marche, #faq, etc.)
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        scrollIntoViewRespectingMotion(el, { block: "start" });
        focusProgrammatically(el);
        return true;
      }
      return false;
    };
    if (!tryScroll()) {
      const t = window.setTimeout(tryScroll, 400);
      return () => window.clearTimeout(t);
    }
  }, [location.hash]);

  const animateStats = () => {
    const duration = 2000;
    const steps = 30;
    const interval = duration / steps;

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setAnimatedStats({
        signalements: Math.floor(finalStats.signalements * progress),
        resolutions: Math.floor(finalStats.resolutions * progress),
        utilisateurs: Math.floor(finalStats.utilisateurs * progress),
        communes: Math.floor(finalStats.communes * progress)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, interval);
  };

  const handleCreateClick = () => {
    setShowCreate(true);
    requestAnimationFrame(() => {
      const section = document.getElementById("signalements");
      if (!section) return;
      scrollIntoViewRespectingMotion(section, { block: "start" });
      const heading = section.querySelector("h2") as HTMLElement | null;
      if (heading) focusProgrammatically(heading);
    });
  };

  const handleHowItWorksClick = () => {
    const section = document.getElementById("comment-ca-marche");
    if (!section) return;
    scrollIntoViewRespectingMotion(section, { block: "start" });
    const heading = section.querySelector("h2") as HTMLElement | null;
    if (heading) focusProgrammatically(heading);
  };

  return (
    <div className="min-h-screen ed-section pb-20 md:pb-0">
      <Helmet>
        <title>KLIKAN — Signalement citoyen à la mairie en 30 secondes</title>
        <meta name="description" content="Signalez à votre mairie nids-de-poule, éclairage, voirie et déchets en 30 s. Suivi temps réel, 76% de résolution. App citoyenne KLIKAN." />
        <meta name="keywords" content="application citoyenne, signalement mairie, signaler nid de poule, civic tech france, application signalement, signalement voirie, signalement éclairage public, déchets sauvages, ville propre" />
        <link rel="canonical" href="https://klikan-fr.lovable.app/" />
        <meta property="og:title" content="KLIKAN — Signalement citoyen à la mairie en 30 secondes" />
        <meta property="og:description" content="L'application civic tech française pour signaler à votre mairie : nids-de-poule, éclairage, voirie, déchets. Suivi temps réel, 76% de résolution." />
        <meta property="og:url" content="https://klikan-fr.lovable.app/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="KLIKAN" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:image" content="https://klikan-fr.lovable.app/og-blog.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="KLIKAN — Signalement citoyen à la mairie en 30 secondes" />
        <meta name="twitter:description" content="Signalez nid de poule, éclairage, voirie à votre mairie. Civic tech française, suivi temps réel." />
        <meta name="twitter:image" content="https://klikan-fr.lovable.app/og-blog.png" />
        <script type="application/ld+json">{JSON.stringify(homeJsonLd)}</script>
      </Helmet>
      <HeroSection showCreate={showCreate} setShowCreate={setShowCreate} />

      <main className="font-body">
        <section id="comment-ca-marche" className="ed-section scroll-mt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <HowItWorksSection onCreateClick={handleCreateClick} />
          </div>
        </section>

        <section id="categories" className="ed-section-cream scroll-mt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <CategoriesShowcase />
          </div>
        </section>

        <section id="impact" className="ed-section scroll-mt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 stats-container">
            <StatsSection animatedStats={animatedStats} />
          </div>
        </section>

        <section id="temoignages" className="ed-section-cream scroll-mt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <TestimonialsSection />
          </div>
        </section>

        <section id="signalements" className="ed-section scroll-mt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            {showCreate ? (
              <div className="max-w-3xl mx-auto">
                <CreateReport />
              </div>
            ) : (
              <div className="space-y-10 md:space-y-14">
                <SearchSection searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                <ReportList searchTerm={searchTerm} />
              </div>
            )}
          </div>
        </section>

        <section id="faq" className="ed-section-cream scroll-mt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <FaqSection />
          </div>
        </section>

        <section id="evenements" className="ed-section scroll-mt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <EventsSection />
          </div>
        </section>

        <section id="communaute" className="ed-section-cream scroll-mt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <CommunitySection />
          </div>
        </section>

        <section id="agir" className="ed-section-dark scroll-mt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
            <FinalCtaSection onCreateClick={handleCreateClick} onHowItWorksClick={handleHowItWorksClick} />
            {/* Maillage interne vers la page d'accès anticipé */}
            <div className="mt-12 pt-8 border-t border-[var(--ed-paper)]/15 text-center">
              <p className="text-sm text-[var(--ed-paper)]/60 mb-3">
                Vous voulez être prévenu du déploiement dans votre commune ?
              </p>
              <a
                href="/launch"
                className="inline-flex items-center gap-2 text-[var(--ed-paper)] font-display font-semibold text-sm tracking-tight hover:underline underline-offset-4"
              >
                Rejoindre la liste d'accès anticipé
                <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <MobileBottomNav onCreateClick={handleCreateClick} />
    </div>
  );
};

export default Index;
